import { useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, BadgeCheck, Factory, Globe2, Landmark, LockKeyhole, ShieldCheck, ShoppingCart, Sparkles, Stethoscope, Truck, Zap } from "lucide-react";
import { Reveal, EASE } from "./shared";
import { SERVICE_SLUGS } from "../i18n";

const serviceIcons = [ShieldCheck, Sparkles, LockKeyhole];
const industryIcons = [Landmark, Stethoscope, ShoppingCart, Factory, Truck, BadgeCheck];
const trustIcons = [ShieldCheck, Zap, Globe2, LockKeyhole];
const industryKeys = ["finance", "healthcare", "retail", "manufacturing", "logistics", "security"];
const trustKeys = ["secure", "fast", "global", "private"];
const trustClasses = ["trust-secure", "trust-fast", "trust-global", "trust-private"];

function Hero({ onContact, t, rtl }) {
  const heroRef = useRef(null);
  const Arrow = rtl ? ArrowLeft : ArrowRight;
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  return (
    <section className="hero section-pad" data-testid="hero-section" ref={heroRef}>
      <motion.div className="orb orb-blue" style={{ y: orbY }} />
      <motion.div className="orb orb-violet" style={{ y: orbY }} />
      <motion.div className="hero-content" style={{ y: contentY }}>
        <motion.div className="hero-kicker" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease: EASE }}><Sparkles size={14} /> {t.kicker}</motion.div>
        <h1>
          <span className="mask"><motion.span className="mask-inner title-main" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, delay: 0.18, ease: EASE }}>VIRA</motion.span></span>
          <em className="mask"><motion.span className="mask-inner title-sub" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, delay: 0.36, ease: EASE }}>{t.tagline}</motion.span></em>
        </h1>
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.62, ease: EASE }}>
          <h2>{t.heroH2}</h2>
          <p>{t.heroP1}<br className="desktop-only" /> {t.heroP2}</p>
          <div className="hero-actions"><a href="#services" className="primary-button" data-testid="hero-services-button">{t.ourServices} <Arrow size={16} /></a><button className="secondary-button" onClick={onContact} data-testid="hero-get-started-button">{t.cta}</button></div>
        </motion.div>
        <motion.div className="stats-row" data-testid="trust-stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.85 }}><div><strong>99.9%</strong><span>{t.stats[0]}</span></div><div><strong>50+</strong><span>{t.stats[1]}</span></div><div><strong>24/7</strong><span>{t.stats[2]}</span></div></motion.div>
      </motion.div>
      {t.trust.map((label, i) => {
        const Icon = trustIcons[i];
        return <motion.div key={trustClasses[i]} className={`floating-trust ${trustClasses[i]}`} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.95 + i * 0.12, ease: EASE }}><Icon size={19} /><span>{label}</span></motion.div>;
      })}
    </section>
  );
}

export function Home() {
  const { t, rtl, openContact } = useOutletContext();
  const Arrow = rtl ? ArrowLeft : ArrowRight;
  return (
    <main id="top">
      <Hero onContact={openContact} t={t} rtl={rtl} />

      <section id="about" className="intro-section section-pad" data-testid="about-section">
        <Reveal><div className="section-label">{t.aboutLabel}</div></Reveal>
        <Reveal delay={0.1}><div className="intro-grid"><h2>{t.aboutH2[0]}<br /><span>{t.aboutH2[1]}</span></h2><p>{t.aboutP}</p></div></Reveal>
      </section>

      <section id="services" className="services-section section-pad" data-testid="services-section">
        <Reveal><div className="section-heading"><div><div className="section-label">{t.servicesLabel}</div><h2>{t.servicesH2[0]}<br /><span>{t.servicesH2[1]}</span></h2></div><p>{t.servicesP}</p></div></Reveal>
        <div className="service-grid">{t.services.map(({ title, text }, index) => {
          const Icon = serviceIcons[index];
          return (
            <Reveal key={title} delay={index * 0.09}>
              <article className="service-card" data-testid={`service-card-${index + 1}`}><div className="service-number">0{index + 1}</div><div className="service-icon"><Icon size={22} /></div><h3>{title}</h3><p>{text}</p><Link to={`/services/${SERVICE_SLUGS[index]}`} data-testid={`service-detail-link-${index + 1}`}>{t.explore} <Arrow size={15} /></Link></article>
            </Reveal>
          );
        })}</div>
      </section>

      <section className="industries-section section-pad" data-testid="industries-section">
        <div className="industries-inner">
          <Reveal><h2>{t.industriesH2}</h2></Reveal>
          <div className="industry-grid">{t.industries.map((title, i) => {
            const Icon = industryIcons[i];
            return (
              <Reveal key={industryKeys[i]} delay={i * 0.06}>
                <article className="industry-card" data-testid={`industry-card-${industryKeys[i]}`}><div className="industry-icon"><Icon size={42} strokeWidth={1.6} /></div><h3>{title}</h3></article>
              </Reveal>
            );
          })}</div>
          <Reveal delay={0.15}><p className="industries-note">{t.industriesNote}</p><button className="primary-button industries-cta" onClick={openContact} data-testid="industries-discuss-button">{t.industriesCta} <Arrow size={22} /></button></Reveal>
        </div>
      </section>

      <section id="why-us" className="why-section section-pad" data-testid="why-us-section">
        <Reveal><div className="why-visual"><div className="grid-lines" /><div className="core-ring"><div className="core-dot" /></div><span className="visual-label label-a">SYSTEM / 01</span><span className="visual-label label-b">ALWAYS ON</span></div></Reveal>
        <Reveal delay={0.12}><div className="why-copy"><div className="section-label">{t.whyLabel}</div><h2>{t.whyH2[0]}<br /><span>{t.whyH2[1]}</span></h2><p>{t.whyP}</p><div className="feature-list">{t.trust.map((label, i) => {
          const Icon = trustIcons[i];
          return <div className="feature-item" key={label} data-testid={`feature-${trustKeys[i]}`}><Icon size={18} /><span>{label}</span><small>{t.featureSub}</small></div>;
        })}</div></div></Reveal>
      </section>

      <section id="contact" className="closing-section section-pad" data-testid="contact-section">
        <div className="closing-orb" />
        <Reveal><div className="section-label">{t.contactLabel}</div><h2>{t.contactH2[0]}<br /><span>{t.contactH2[1]}</span></h2><button className="primary-button" onClick={openContact} data-testid="contact-section-button">{t.contactCta} <Arrow size={17} /></button></Reveal>
      </section>
    </main>
  );
}
